import {
  clearResult,
  deleteAttachmentFromRoomByKey,
  finishReceivingMessages,
  interruptTextToSpeech,
  playTextToSpeech,
  resetAttachments,
  resetTextToSpeech,
  sendAttachmentToRoom,
  sendMessageToRoom,
  setHistrotyResults,
  setIsReceivingMessages,
  startReceivingMessages,
  startVoiceChatMode,
  stopTextToSpeech,
  stopVoiceChatMode,
  toggleVoiceChatMode,
  updateResultWithBot,
  updateResultWithSearchQuery,
  updateTextToSpeechLoading,
  voiceChatModeListener,
  voiceModChat,
} from '@webclient/state/slices/search-session';
import connectAsyncToRoom from '@webclient/state/slices/search-session/thunks/connectAsyncToRoom';
import searchAsyncOrchestrator from '@webclient/state/slices/search-session/thunks/searchAsyncOrchestrator';
import { roomInstance } from '@webclient/state/slices/search-session/utils/room-manager';
import { AppDispatch, RootType } from '@webclient/state/store';

import {
  CustomStyleType,
  ResearchAdvancedTimeType,
  ResponseLengthType,
} from '@webclient/shared-types/auth';
import { BotsResponseType } from '@webclient/shared-types/bots';
import {
  SearchModeType,
  SearchScopeType,
} from '@webclient/shared-types/search-result/types';
import { VoiceType } from '@webclient/shared-types/settings';

import {
  isKrispNoiseFilterSupported,
  KrispNoiseFilter,
} from '@livekit/krisp-noise-filter';
import {
  DataPacket_Kind,
  LocalAudioTrack,
  Participant,
  Room,
  RoomEvent,
} from 'livekit-client';
import { Middleware } from 'redux';

type SendMessageToRoomType = {
  prompt: string;
  files: { key: string; mimeType: string }[];
  customStyle: Omit<CustomStyleType, 'isUpToDate'> | null;
  researchAdvancedTime: ResearchAdvancedTimeType;
  responseLength: ResponseLengthType;
  searchMode: SearchModeType;
  searchScope: SearchScopeType;
  botId: BotsResponseType[number]['id'];
  tts: {
    voice: VoiceType;
    speed: number;
  } | null;
};

const searchSessionMiddleware: Middleware = ({
  getState,
  dispatch,
}: {
  dispatch: AppDispatch;
  getState: () => RootType;
}) => {
  return (next) => async (action) => {
    const me = getState().auth.me;
    if (sendMessageToRoom.match(action)) {
      const state = getState();
      const searchSessionResultState = state.searchSession.result;
      const isRoomConnected = state.searchSession.isConnected;
      const prompt = action.payload;
      const shouldPushResultToHistory =
        !!searchSessionResultState.historyBlockId;

      if (shouldPushResultToHistory) {
        dispatch(
          setHistrotyResults({
            botSwitches: searchSessionResultState.botSwitches,
            bot: searchSessionResultState.bot,
            id: searchSessionResultState.historyBlockId!,
            searchQuery: searchSessionResultState.searchQuery,
            followups: searchSessionResultState.followups,
            layout: {
              answer: {
                id: searchSessionResultState.layout.answer?.id,
                text: searchSessionResultState.layout.answer?.text,
              },
              photo: searchSessionResultState.layout.photo && {
                id: searchSessionResultState.layout.photo.id,
                photos: searchSessionResultState.layout.photo.photos,
              },
              source: searchSessionResultState.layout.source && {
                id: searchSessionResultState.layout.source.id,
                source: searchSessionResultState.layout.source.source,
              },
            },
          }),
        );
        dispatch(clearResult());
      }

      dispatch(updateResultWithSearchQuery(prompt));

      if (!isRoomConnected) {
        throw new Error('Room is not connected');
      }

      if (!me) {
        throw new Error('Token is not found');
      }

      const files = getState().searchSession.attachments.map((attachment) => ({
        key: attachment.key!,
        mimeType: attachment.type,
        name: attachment.name,
      }));

      const {
        searchMode,
        searchScope,
        useCustomStyle,
        responseLength,
        researchAdvancedTime,
        customStyle,
        bot,
        voice,
      } = getState().appSettings.userSpecificSettings[me.uuid]!;

      const dataToSend: SendMessageToRoomType = {
        prompt,
        files,
        searchScope,
        searchMode,
        customStyle: useCustomStyle
          ? {
              responseStyle: customStyle.responseStyle,
              id: customStyle.id,
            }
          : null,
        researchAdvancedTime,
        responseLength,
        botId: bot.id,
        tts: voice.autoplay
          ? {
              voice: voice.type,
              // speed: voice.speed, //TODO: uncomment when speed is ready in knowz-ai
              speed: 1,
            }
          : null,
      };

      const encodedData = new TextEncoder().encode(JSON.stringify(dataToSend));
      const room = roomInstance.get();
      if (room == null) {
        console.error('Error: room is null');
        return;
      }

      room.localParticipant.publishData(encodedData, {
        reliable: true,
        topic: 'search',
      });

      if (files.length > 0) {
        dispatch(resetAttachments());
      }
    }
    if (voiceModChat.match(action)) {
      const { realtime, bot } = action.payload;
      const publishToggle = (room: Room) => {
        if (!me) {
          throw new Error('Token is not found');
        }
        const { searchScope } =
          getState().appSettings.userSpecificSettings[me.uuid];

        const data = JSON.stringify({ realtime, botId: bot?.id, searchScope });
        room.localParticipant.publishData(new TextEncoder().encode(data), {
          reliable: true,
          topic: 'set_realtime_mode',
        });
      };
      const state = getState();
      const isConnected = state.searchSession.isConnected;
      if (!isConnected) {
        dispatch(
          searchAsyncOrchestrator({
            requestOrigin: 'search',
            withAudio: false,
          }),
        ).then(() => {
          const connectedRoom = roomInstance.get();
          if (!connectedRoom) {
            console.error('Error: room is null after searchAsyncOrchestrator');
          } else {
            publishToggle(connectedRoom);
          }
        });
      } else {
        const room = roomInstance.get();
        if (!room) {
          dispatch(connectAsyncToRoom({ audio: false })).then(() => {
            const reconnected = roomInstance.get();
            if (!reconnected) {
              console.error('Error: room is null after connectAsyncToRoom');
            } else {
              publishToggle(reconnected);
            }
          });
        } else {
          publishToggle(room);
        }
      }
    }

    if (startReceivingMessages.match(action)) {
      if (!me) {
        throw new Error('Token is not found');
      }
      const { bot } = getState().appSettings.userSpecificSettings[me.uuid]!;
      dispatch(updateResultWithBot(bot));
      dispatch(setIsReceivingMessages(true));
    }

    if (finishReceivingMessages.match(action)) {
      dispatch(setIsReceivingMessages(false));
    }

    if (playTextToSpeech.match(action)) {
      const {
        payload: { blockId, ...sendToLivekit },
      } = action as ReturnType<typeof playTextToSpeech>;
      const room = roomInstance.get();
      const textEncoder = new TextEncoder();
      if (room == null) {
        console.error('Error: room is null');
        return;
      }

      dispatch(updateTextToSpeechLoading(true));
      room.localParticipant.publishData(
        textEncoder.encode(
          JSON.stringify({
            ...sendToLivekit,
            blockId,
          }),
        ),
        {
          reliable: true,
          topic: 'tts_play',
        },
      );
    }

    if (stopTextToSpeech.match(action)) {
      dispatch(resetTextToSpeech());
    }

    if (interruptTextToSpeech.match(action)) {
      dispatch(resetTextToSpeech());
      const room = roomInstance.get();
      const textEncoder = new TextEncoder();
      if (room == null) {
        console.error('Error: room is null');
        return;
      }
      room.localParticipant.publishData(textEncoder.encode(), {
        reliable: true,
        topic: 'interrupt_tts',
      });
    }

    if (sendAttachmentToRoom.match(action)) {
      const room = roomInstance.get();
      const encodedData = new TextEncoder().encode(
        JSON.stringify(action.payload),
      );
      room?.localParticipant.publishData(encodedData, {
        reliable: true,
        topic: 'add_file',
      });
    }

    if (deleteAttachmentFromRoomByKey.match(action)) {
      const room = roomInstance.get();

      const encodedData = new TextEncoder().encode(
        JSON.stringify(action.payload),
      );
      room?.localParticipant.publishData(encodedData, {
        reliable: true,
        topic: 'remove_file',
      });
    }

    function handleRecieveData(
      payload: Uint8Array,
      _participant?: Participant,
      _kind?: DataPacket_Kind,
      topic?: string,
    ) {
      //TODO: we need this for [T] in the design to drop trascription to the textarea https://www.figma.com/design/UvxSnGzKybTC3h9jCjMXTv/KNOWZ---HOME-SEARCH?node-id=2031-563250&t=pCQ3F8Gm0XoWelKA-0
      const decoder = new TextDecoder();
      const decodedPayload = decoder.decode(payload);
      const parsedData = JSON.parse(decodedPayload) as {
        text: string;
      };
      const typedTopic = topic as 'stt_temp' | 'stt_sentence';

      if (isKrispNoiseFilterSupported()) {
        const krispProcessor = KrispNoiseFilter();
        krispProcessor.setEnabled(true);
      } else {
        console.error(
          'Krisp noise filter is currently not supported on this browser',
        );
      }

      if (typedTopic === 'stt_sentence') {
        dispatch(updateResultWithSearchQuery(parsedData.text));
      }
    }

    if (startVoiceChatMode.match(action)) {
      const isAlreadyOn = getState().searchSession.voiceChatMode.mode;
      if (isAlreadyOn) {
        return next(action);
      }
      const room = roomInstance.get();

      if (!room) {
        console.error('No room found');
        return;
      }

      voiceChatModeListener.startListening({
        predicate: startVoiceChatMode.match,
        effect: (_) => {
          roomInstance.get()?.on(RoomEvent.DataReceived, handleRecieveData);
        },
      });

      const audioPublication = room.localParticipant
        .getTrackPublications()
        .find((pub) => pub.track?.source === 'microphone');

      if (
        audioPublication &&
        audioPublication.track instanceof LocalAudioTrack
      ) {
        room.localParticipant.setMicrophoneEnabled(true);
        audioPublication.track.resumeUpstream();
      } else {
        room.localParticipant.setMicrophoneEnabled(true);
      }

      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ modules: ['stt'] })),
        {
          reliable: true,
          topic: 'add_modules',
        },
      );

      dispatch(toggleVoiceChatMode(true));
    }

    if (stopVoiceChatMode.match(action)) {
      const room = roomInstance.get();
      dispatch(clearResult());
      voiceChatModeListener.stopListening({
        predicate: stopVoiceChatMode.match,
        effect: (_) => {
          roomInstance.get()?.off(RoomEvent.DataReceived, handleRecieveData);
        },
      });

      if (!room) {
        console.error('No room found');
        return;
      }
      const audioPublication = room.localParticipant
        .getTrackPublications()
        .find((pub) => pub.track?.source === 'microphone');

      if (
        audioPublication &&
        audioPublication.track instanceof LocalAudioTrack
      ) {
        room.localParticipant.setMicrophoneEnabled(false);
        audioPublication.track.stop();
      }

      room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ modules: ['stt'] })),
        {
          reliable: true,
          topic: 'remove_modules',
        },
      );
      dispatch(toggleVoiceChatMode(false));
    }

    next(action);
  };
};

export default searchSessionMiddleware;
